<template>
  <div
    :class="[
      $style.base,
      {
        [$style.standalone]: standalone,
        [$style.isAny]: !employee
      }
    ]"
  >
    <div :class="$style.inner">
      <BaseAvatar :employee="employee" />
      <div :class="$style.content">
        <div :class="$style.employeeInfo">
          <div
            :class="$style.name"
            :style="
              !employee
                ? {
                    color: widgetSettings.color
                  }
                : null
            "
          >
            {{
              !employee
                ? employeeSelectionRequired
                  ? $t('availabilities.choose_employee_heading')
                  : $t('availabilities.any_employee')
                : employee.name
            }}
          </div>
          <div v-if="!standalone">
            <div v-if="!employee" :class="$style.sub">
              {{ $t('availabilities.maximum_availability') }}
            </div>
            <div
              v-else
              :class="[$style.sub, $style.availability]"
              v-test="'resource-item-availability'"
            >
              <span v-if="0 <= daysTillAvailable && daysTillAvailable < 14">
                {{
                  $t(
                    'availabilities.x_days',
                    { days: daysTillAvailable },
                    daysTillAvailable
                  )
                }}
              </span>
              <span v-else-if="availabilities.length">
                {{ $t('availabilities.more_than_14_days') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Employee } from '@/types';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { useAvailabilityStore } from '@/stores/availability';
import { useCompanyStore } from '@/stores/company';
import widgetSettings from '@/widget-settings';

import BaseAvatar from '@/components/BaseAvatar.vue';

const props = defineProps<{
  employee?: Employee;
  standalone?: boolean;
}>();

const { employeeSelectionRequired } = useCompanyStore();

const { availabilities } = storeToRefs(useAvailabilityStore());

const daysTillAvailable = computed(() => {
  if (!props.employee) {
    return 0;
  }

  return availabilities.value.findIndex(
    (item) =>
      item.available_resources?.includes(props.employee.id) &&
      item.timeslots.some((slot) =>
        slot.resource_ids.includes(props.employee.id)
      )
  );
});
</script>

<style lang="scss" module>
.base {
  position: relative;
  z-index: 1;
  background-color: white;
  border-top: 1px solid $color-border;
  cursor: pointer;

  &.standalone:first-child {
    border-top: none;
  }
}

.inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing;
  height: $dropdown-item-height;
}

.content {
  width: 100%;
  display: flex;
  justify-content: space-between;
  min-width: 0;
}

.employeeInfo {
  overflow: hidden;
  margin-right: $spacing;
}
.name {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  padding-bottom: 3px;

  .base.isAny & {
    font-weight: bold;
  }
}
.availability {
  font-size: 13px;
  white-space: nowrap;
}
.sub {
  margin-top: 4px;
  margin-bottom: 4px;
  font-size: 12px;
  @include font-sub;
}
</style>
